import { Component, OnDestroy } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Data, Router } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { LoadingService } from "@core/loading.service";
import { ModalController, NavController, IonicModule } from "@ionic/angular";
import { TranslateService, TranslatePipe } from "@ngx-translate/core";
import { CommonVariable } from "src/app/app.constants";
import { PermanentlyCloseComponent } from "src/app/components/permanently-close-popup/permanently-close-popup.component";
import { AccountRepositoryService } from "../account-repository.service";
import { DeleteDataComponent } from "src/app/components/delete-data/delete-data.component";
import { Subject, take, takeUntil } from "rxjs";
import { SafeHtmlPipe } from "../../../pipe/safehtmlpipe";

@Component({
    templateUrl: "./delete-confirmation.component.html",
    styleUrls: ["./delete-confirmation.component.scss"],
    standalone: true,
    imports: [
        IonicModule,
        SafeHtmlPipe,
        TranslatePipe,
    ],
})
export class DeleteConfirmationComponent implements OnDestroy {
  syndicateLoaded: boolean;
  jackpotLoaded: boolean;

  setActive = true;
  setActiveTrigger = true;
  translatedToastMsg: Data;
  selectedLanguage = "en";
  public preferredCurrency: string;
  public currencySymbol: string = CommonVariable.defaultCurrencySymbol;
  public spinnerAnimation: boolean;
  public progressBar: number;
  public sub: Data;
  public proceed = true;
  errorAlert = false;
  acceptanceCheckbox = false;
  private unsubscribe$ = new Subject<void>();

  constructor(
    public translate: TranslateService,
    private authService: AuthService,
    private navController: NavController,
    private accountRepository: AccountRepositoryService,
    private modalController: ModalController,
    private loadingService: LoadingService,
    private router: Router,
    private matDialog: MatDialog
  ) {
    this.preferredCurrency = CommonVariable.preferredCurrency;
    this.currencySymbol = CommonVariable.defaultCurrencySymbol;
    this.authService.selectedlanguage$.pipe(takeUntil(this.unsubscribe$)).subscribe(
      (l) => {
        this.selectedLanguage = l;
        translate.use(l ? l : "en");
      },
      (err) => {
        return err;
      }
    );
  }
  ngOnDestroy(): void {
    this.unsubscribe$?.next();
    this.unsubscribe$?.complete();
  }

  goToGetHelpPage() {
    this.router.navigateByUrl("account/helpSupport?bot_flow=hi");
  }

  onCheck(event) {
    const value = event?.detail?.checked;
    this.acceptanceCheckbox = value;
  }

  goBack() {
    this.navController.back();
  }

  onDelete() {
    if (!this.acceptanceCheckbox) {
      this.errorAlert = true;
      return;
    }
    this.errorAlert = false;
    this.accountRepository
      .getUserPreferences()
      .pipe(take(1))
      .subscribe((value) => {
        const action = value?.content?.[1]?.close_other_options?.[0]?.close_options?.[1]?.title;
        this.updateLegalPreferences(action);
      });
  }

  updateLegalPreferences(action: string) {
    if (action === "Delete all of my data") {
      const confirmationDialog = this.matDialog.open(DeleteDataComponent, {
        panelClass: "remove-wallet-dialog",
        backdropClass: "cashout-back-drop-color",
      });

      confirmationDialog.afterClosed().subscribe(
        async (dialogResponse: boolean) => {
          if (dialogResponse) {
            this.insertGDPRAction(action, "delete_my_personal_data");
          }
        },
        (err) => {
          return err;
        }
      );
    }
  }

  insertGDPRAction(action: string, moduleName: string) {
    this.loadingService.present();
    this.accountRepository.insertUserGDPR(action, moduleName).subscribe(
      (gdprRes) => {
        if (gdprRes?.status === "rejected") {
          this.loadingService.dismiss();
          this.authService
            .getConstantsMsg(gdprRes?.error_code)
            .then((translatedMsg) => {
              const defaultMsg = {
                message: {
                  line_1: "Something went wrong!",
                  line_2: "",
                },
                style: {
                  icon: "tick circle error.svg",
                  type: "Error",
                },
              };
              this.authService.showToastMessage(translatedMsg, defaultMsg);
            })
            .catch((error) => {
              return error;
            });
          return;
        }
        if (gdprRes && gdprRes?.response?.status?.toLowerCase() === "success") {
          try {
            this.loadingService.dismiss().catch((err) => {
              return err;
            });
            const confirmationDialog = this.matDialog.open(PermanentlyCloseComponent, {
              panelClass: "remove-confirmation-popup",
              backdropClass: "back-drop-popup",
              enterAnimationDuration: "500ms",
              exitAnimationDuration: "500ms",
              data: "deleted",
            });

            confirmationDialog.afterClosed().subscribe(
              () => {
                this.modalController.dismiss(true).catch((err) => {
                  return err;
                });
                // This method has been used to redirect the user logout
                this.authService
                  .logout()
                  .then(() => {
                    // This method has been used to redirect the user after logout on the welcome page
                    this.router.navigateByUrl("/welcome");
                  })
                  .catch((error) => {
                    return error;
                  });
              },
              (err) => {
                return err;
              }
            );
          } catch (error) {
            return error;
          }
        } else {
          this.loadingService.dismiss().catch((err) => {
            return err;
          });
          this.authService
            .getConstantsMsg("1014")
            .then((translatedMsg) => {
              const defaultMsg = {
                message: {
                  line_1: "Something went wrong",
                  line_2: "",
                },
                style: {
                  type: "Error",
                  icon: "tick circle error.svg",
                },
              };
              this.authService.showToastMessage(translatedMsg, defaultMsg);
            })
            .catch((error) => {
              return error;
            });
        }
      },
      () => {
        this.loadingService.dismiss().catch((err) => {
          return err;
        });
        this.authService
          .getConstantsMsg("1014")
          .then((translatedMsg) => {
            const defaultMsg = {
              message: {
                line_1: "Something went wrong",
                line_2: "",
              },
              style: {
                type: "Error",
                icon: "tick circle error.svg",
              },
            };
            this.authService.showToastMessage(translatedMsg, defaultMsg);
          })
          .catch((error) => {
            return error;
          });
      }
    );
  }
}
